import { useState } from 'react';
import { downloadArtifact } from '../api';
import { ArtifactViewer } from './ArtifactViewer';
import { STATUS_LABEL, type ArtifactName, type Job } from '../types';

interface Props {
  job: Job;
  token: string;
  expanded: boolean;
  onToggle: () => void;
}

const ARTIFACT_LIST: { artifact: ArtifactName; label: string; hint: string }[] = [
  { artifact: 'todos', label: 'Todos', hint: 'Aufgaben nach Person gruppiert' },
  { artifact: 'summary', label: 'Zusammenfassung', hint: 'Kernpunkte, Entscheidungen, offene Fragen' },
  { artifact: 'transcript', label: 'Transkript', hint: 'Vollständig, mit Sprechern und Zeitstempeln' },
  { artifact: 'bundle', label: 'Gesamtprotokoll', hint: 'Alles in einer Datei' },
  { artifact: 'todos-json', label: 'Todos (JSON)', hint: 'Zur Weiterverarbeitung' },
];

function formatDuration(seconds?: number): string | undefined {
  if (!seconds) return undefined;
  const hh = Math.floor(seconds / 3600);
  const mm = Math.floor((seconds % 3600) / 60);
  return hh > 0 ? `${hh} h ${mm} min` : `${mm} min`;
}

export function JobCard({ job, token, expanded, onToggle }: Props) {
  const [downloadError, setDownloadError] = useState<string>();
  const [viewing, setViewing] = useState<ArtifactName>();

  const meta = [
    formatDuration(job.durationSeconds),
    job.speakerCount ? `${job.speakerCount} Sprecher` : undefined,
    job.todoCount !== undefined ? `${job.todoCount} Todos` : undefined,
    job.markerCount ? `${job.markerCount}× fürs Protokoll markiert` : undefined,
    new Date(job.createdAt).toLocaleString('de-DE'),
  ].filter(Boolean);

  const available = ARTIFACT_LIST.filter((entry) => job.artifacts?.[entry.artifact]);

  async function download(artifact: ArtifactName) {
    setDownloadError(undefined);
    try {
      await downloadArtifact(token, job.jobId, artifact);
    } catch (err) {
      setDownloadError(err instanceof Error ? err.message : 'Download fehlgeschlagen');
    }
  }

  return (
    <li className={`job job-${job.status.toLowerCase()}`}>
      <button className="job-head" onClick={onToggle} aria-expanded={expanded}>
        <span className="job-title">
          <strong>{job.title ?? job.fileName}</strong>
          {job.title && <span className="muted small"> · {job.fileName}</span>}
        </span>
        <span className={`badge badge-${job.status.toLowerCase()}`}>{STATUS_LABEL[job.status]}</span>
      </button>

      <p className="muted small job-meta">{meta.join(' · ')}</p>

      {job.status === 'FAILED' && job.error && <p className="error">{job.error}</p>}

      {expanded && (
        <div className="job-body">
          {available.length > 0 ? (
            <ul className="artifacts">
              {available.map((entry) => (
                <li key={entry.artifact}>
                  <span className="artifact-label">
                    <strong>{entry.label}</strong>
                    <span className="muted small">{entry.hint}</span>
                  </span>
                  <span className="artifact-actions">
                    <button onClick={() => setViewing(entry.artifact)}>Ansehen</button>
                    <button onClick={() => void download(entry.artifact)}>Herunterladen</button>
                  </span>
                </li>
              ))}
            </ul>
          ) : (
            <p className="muted">Noch keine Dateien verfügbar.</p>
          )}
          {downloadError && <p className="error">{downloadError}</p>}
        </div>
      )}

      {viewing && (
        <ArtifactViewer
          token={token}
          jobId={job.jobId}
          artifact={viewing}
          title={`${ARTIFACT_LIST.find((e) => e.artifact === viewing)?.label} · ${job.title ?? job.fileName}`}
          onClose={() => setViewing(undefined)}
        />
      )}
    </li>
  );
}
